/**
 * Recording a clip in the app, rather than handing the phone's camera off.
 *
 * An iPhone records for a web page at its "medium" quality when the page opens
 * the camera through a file input: about 1 Mbps, well under 1080p. Recorded
 * here the page chooses: 1080p, and a bitrate a 15-second snap can afford.
 */

/** Plenty for 1080p at 30fps; a full 15-second snap comes to about 19MB. */
export const RECORD_VIDEO_BITS = 10_000_000;
export const RECORD_AUDIO_BITS = 128_000;

/** What this browser can record into, best first: MP4 plays everywhere. */
export function recordingType(): string | undefined {
  if (typeof MediaRecorder === 'undefined') return undefined;
  return ['video/mp4;codecs=avc1', 'video/mp4', 'video/webm;codecs=h264', 'video/webm'].find((type) =>
    MediaRecorder.isTypeSupported(type),
  );
}

/** Whether the app can record here at all; if not, the phone's camera does. */
export function canRecordInApp(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    typeof navigator.mediaDevices?.getUserMedia === 'function' &&
    recordingType() !== undefined
  );
}

/** The camera to ask for: 1080p at 30fps, back or front. */
export function cameraConstraints(facing: 'environment' | 'user'): MediaStreamConstraints {
  return {
    video: {
      facingMode: facing,
      width: { ideal: 1920 },
      height: { ideal: 1080 },
      frameRate: { ideal: 30 },
    },
    audio: true,
  };
}

/**
 * The recording as a file the upload takes. The plain type, without the codec
 * list after it, because that is what the upload is checked against.
 */
export function recordedFile(parts: Blob[], type: string): File {
  const plain = type.split(';')[0].trim() || 'video/webm';
  const extension = plain === 'video/mp4' ? 'mp4' : 'webm';
  return new File(parts, `snap.${extension}`, { type: plain });
}
